import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import * as urls from '../utilities/urls';
import { User, RefreshToken } from '../models/auth-user';
import { AuthRoutes } from '../utilities/routes';
import { Address, appCodeValue, UsersType } from '../utilities/common-file';
import { Subject } from 'rxjs';
import { EncryptStorage} from 'encrypt-storage';
import { SystemSearch } from '../models/System';
import { NzTableData } from '../utilities/nz-table.adapter';
import { NzModalService } from 'ng-zorro-antd/modal';
export const encryptStorage = EncryptStorage('secret_key', {
  storageType: 'localStorage'
});
@Injectable({
    providedIn: 'root',
})
export class AuthService {
    constructor(private httpClient: HttpClient, private router: Router, private modalService: NzModalService) {}

    private static AUTH_USER = 'AUTH_USER';
    private static AUTH_USER_ID = 'AUTH_USER_ID';
    private static AUTH_USER_DISPLAY_NAME = 'AUTH_USER_DISPLAY_NAME';
    private static AUTH_TOKEN = 'AUTH_TOKEN';
    private static REFRESH_TOKEN = 'REFRESH_TOKEN';
    private static AUTH_USER_MERCHANT_ID = 'AUTH_USER_MERCHANT_ID';
    private static AUTH_USER_MERCHANT_NAME = 'AUTH_USER_MERCHANT_NAME';
    private static AUTH_USER_LOCATION_ID = 'AUTH_USER_LOCATION_ID';
    private static AUTH_USER_LOCATION_NAME = 'AUTH_USER_LOCATION_NAME';
    private static AUTH_USER_ROLE = 'AUTH_USER_ROLE';
    private static AUTH_USER_APPCODE = 'AUTH_USER_APPCODE';
    private static AUTH_USER_EMAIL = 'AUTH_USER_EMAIL';
    private static MERCHANT_DETAILS = 'MERCHANT_DETAILS';
    private static LOCATION_DETAILS = 'LOCATION_DETAILS';
    private static TERMINAL_DETAILS = 'TERMINAL_DETAILS';
    private static AUTH_USER_PASSWORD = 'AUTH_USER_PASSWORD';
    private static AUTH_USER_REMEMBERME = 'AUTH_USER_REMEMBERME';
    private static DEVICE_SIGNATURE = 'DEVICE_SIGNATURE';
    private static AUTH_USER_ROLE_ID = 'AUTH_USER_ROLE_ID';
    private static AUTH_ACCESS_MODULES = 'AUTH_ACCESS_MODULES';

    isRememeberMe = false;

    accessModuleList = [];
    public notify: Subject<boolean> = new Subject<boolean>(); // To emit an event on Logout

    login(value: any): Observable<User> {
        return this.httpClient.post<User>(urls.AuthUrls.login, value).pipe(tap((authUser) => {
            if (value.rememberMe) {
                this.isRememeberMe = value.rememberMe;
                encryptStorage.clear();
                encryptStorage.setItem(AuthService.AUTH_USER_REMEMBERME, value.rememberMe);

                 // To Set encrypted password in local storage if remember me is checked
                encryptStorage.setItem(AuthService.AUTH_USER_PASSWORD, value.password);

                if (authUser.message === 'OTP is already sent to your device. Please contact customer support') {
                    this.router.navigate([AuthRoutes.otpVerify + value.email]);
                   } else if (authUser && authUser.data && authUser.data.isOTPSent) {
                    this.router.navigate([AuthRoutes.otpVerify + value.email]);
                   } else {
                    this.setUser(authUser);
                   }
              } else {
                this.isRememeberMe = false;
                encryptStorage.clear();
                if (authUser.message === 'OTP is already sent to your device. Please contact customer support') {
                    this.router.navigate([AuthRoutes.otpVerify + value.email]);
                   } else if (authUser && authUser.data && authUser.data.isOTPSent) {
                    this.router.navigate([AuthRoutes.otpVerify + value.email]);
                   } else {
                    this.setUser(authUser);
                   }
              }
        }));
    }
    refreshTokens(): Observable<RefreshToken> {
        return new Observable(subscriber => {
          this.httpClient.get(urls.AuthUrls.refreshTokens)
            .pipe(tap((refreshToken: any) => {
              this.setRefreshToken(refreshToken.data.refreshToken);
            }))
            .subscribe(authUser => {
              subscriber.next(authUser);
              subscriber.complete();
            });
        });
      }
    refreshCustomTokens(): Observable<any> {
        return new Observable(subscriber => {
            this.httpClient
            .post<any>(urls.AuthUrls.refreshCustomToken, {
                refreshToken: this.getRefreshToken(),
            })
            .pipe(tap((authUser) => {
               // tslint:disable-next-line: no-non-null-assertion
          if (authUser.data && authUser.data.refresh_token && authUser.data.refresh_token !== '') {
            this.setAuthToken(authUser.data.refresh_token);
          } else {
            this.onNotify(true);
            encryptStorage.clear();
            this.router.navigate([AuthRoutes.login]);
            this.showMessage();
          }
            }))
            .subscribe(authUser => {
                subscriber.next(authUser);
                subscriber.complete();
            });
        });
    }
    verifyOTP(value): Observable<any> {
        return this.httpClient.post<User>(urls.AuthUrls.verifyOTP, value).pipe(tap((authUser) => {
          this.setUser(authUser);
        }));
      }
      resendOTP(value) {
        return this.httpClient.post(urls.AuthUrls.resendOTP, value);
      }
    forgetPassword(value) {
        return this.httpClient.post(urls.AuthUrls.forgetPassword, value);
    }
    resetPassword(value) {
        return this.httpClient.post(urls.AuthUrls.resetPassword, value);
    }
    getSystemWithLimit(tableData: NzTableData<SystemSearch, any>) {
      const value = {
          appCode: tableData.search.appCode,
          pageSize: tableData.pageSize,
          pageIndex: tableData.pageIndex,
          name: tableData.search.name,
          displayName: tableData.search.displayName
      };
      return this.httpClient.post(urls.systemUrls.list, value);
  }
    validateToken(value) {
        return this.httpClient.post(urls.AuthUrls.tokenValidate, value);
    }
    private setUser(authUser) {
        encryptStorage.setItem(AuthService.AUTH_USER, JSON.stringify(authUser));
        encryptStorage.setItem(AuthService.AUTH_USER_ID, authUser.data.id);
        encryptStorage.setItem(AuthService.AUTH_USER_EMAIL, authUser.data.email);
        encryptStorage.setItem(AuthService.AUTH_USER_DISPLAY_NAME, authUser.data.displayName);
        encryptStorage.setItem(AuthService.AUTH_USER_ROLE, authUser.data.role);
        encryptStorage.setItem(AuthService.AUTH_USER_APPCODE, authUser.data.appCode);
        this.setAuthToken(authUser.data.customToken);
        let appCode = '';
        appCode = encryptStorage.getItem(AuthService.AUTH_USER_APPCODE);
        if (appCode === appCodeValue.all) {
            encryptStorage.setItem(AuthService.AUTH_USER_APPCODE, '');
        }
        if (authUser.data.merchant) {
            encryptStorage.setItem(AuthService.AUTH_USER_MERCHANT_ID, authUser.data.merchant.id);
            encryptStorage.setItem(AuthService.AUTH_USER_MERCHANT_NAME, authUser.data.merchant.name);
            encryptStorage.setItem(AuthService.MERCHANT_DETAILS, authUser.data.merchant);
        }
        if (authUser && authUser.data && authUser.data.roleId) {
          encryptStorage.setItem(AuthService.AUTH_USER_ROLE_ID, authUser.data.roleId);
        }
        // Set Location & Terminal for filters dropdown

        if (authUser.data && authUser.data.role && authUser.data.role === UsersType.merchantUser) {
            if (authUser.data.locations) {
                encryptStorage.setItem(AuthService.LOCATION_DETAILS, authUser.data.locations);
            }
            if (authUser.data.terminals) {
                encryptStorage.setItem(AuthService.TERMINAL_DETAILS, authUser.data.terminals);
            }
        }
        if (authUser.data && authUser.data.role && authUser.data.role !== UsersType.merchantUser) {
            if (authUser.data.location) {
                encryptStorage.setItem(AuthService.AUTH_USER_LOCATION_ID, authUser.data.location.id);
                encryptStorage.setItem(AuthService.AUTH_USER_LOCATION_NAME, authUser.data.location.name);
                const locationDetails = [authUser.data.location];
                encryptStorage.setItem(AuthService.LOCATION_DETAILS, locationDetails);
            }
            if (authUser.data.terminal) {
            const terminalDetails = [authUser.data.terminal];
            encryptStorage.setItem(AuthService.TERMINAL_DETAILS, terminalDetails);
          }
        }
    }
    setDeviceSignature(value) {
      localStorage.setItem(AuthService.DEVICE_SIGNATURE, value);
    }
    getDeviceSignature() {
      return localStorage.getItem(AuthService.DEVICE_SIGNATURE);
    }
    getPassword(): string {
      return encryptStorage.getItem(AuthService.AUTH_USER_PASSWORD);
    }
    getRememberMe() {
      return encryptStorage.getItem(AuthService.AUTH_USER_REMEMBERME);
    }
    getUserId(): number {
        return Number(encryptStorage.getItem(AuthService.AUTH_USER_ID));
    }
    getDisplayName(): string {
        return encryptStorage.getItem(AuthService.AUTH_USER_DISPLAY_NAME);
    }
    setDisplayName(name: string) {
        encryptStorage.setItem(AuthService.AUTH_USER_DISPLAY_NAME, name);
    }
    getEmail(): string {
        return encryptStorage.getItem(AuthService.AUTH_USER_EMAIL);
    }
    getRole(): string {
        return encryptStorage.getItem(AuthService.AUTH_USER_ROLE);
    }
    getMerchantId(): any {
        // tslint:disable-next-line: radix
        return parseInt(encryptStorage.getItem(AuthService.AUTH_USER_MERCHANT_ID));
    }
    getLocationId(): any {
        // tslint:disable-next-line: radix
        return parseInt(encryptStorage.getItem(AuthService.AUTH_USER_LOCATION_ID));
    }
    getMerchantDetails(): any {
        return encryptStorage.getItem(AuthService.MERCHANT_DETAILS);
    }
    getLocationDetails(): any {
        return encryptStorage.getItem(AuthService.LOCATION_DETAILS);
    }
    getTerminalDetails(): any {
        return encryptStorage.getItem(AuthService.TERMINAL_DETAILS);
    }
    getRoleAccessModule(): string {
      return encryptStorage.getItem(AuthService.AUTH_ACCESS_MODULES);
    }
    getRoleId(): string {
      return encryptStorage.getItem(AuthService.AUTH_USER_ROLE_ID);
    } 
    setRoleAccessModule(type: string) {
      encryptStorage.setItem(AuthService.AUTH_ACCESS_MODULES, type);
    }
    
    getAppCode() {
        let appCode =  encryptStorage.getItem(AuthService.AUTH_USER_APPCODE);
        if (appCode && typeof appCode === 'string' || appCode ==  '') {
            return encryptStorage.getItem(AuthService.AUTH_USER_APPCODE);
        } else {
            return encryptStorage.getItem(AuthService.AUTH_USER_APPCODE).toString();
        }
    }
      // To get address dynamically.
  getAddressName(data){
    let address = Address[data.appCode] ? Address[data.appCode]:Address[''];
   return address;
 }
    private setAuthToken(token: string) {
        encryptStorage.setItem(AuthService.AUTH_TOKEN, token);
    }

    getAuthToken(): string {
        return encryptStorage.getItem(AuthService.AUTH_TOKEN);
    }

    private setRefreshToken(token: string) {
        encryptStorage.setItem(AuthService.REFRESH_TOKEN, token);
    }

    getRefreshToken(): string {
        return encryptStorage.getItem(AuthService.REFRESH_TOKEN);
    }
    // This function will return true When Logout is detected
    onNotify(event) {
        return this.notify.next(true);
    }
    // For Login User Type based options show / Hide

    showOptionsBasedOnUserType() {
    const userType = this.getRole();
    if (userType === UsersType.locationUser || userType === UsersType.locationManager) {
        return false;
    } else {
        return true;
    }
    }

    logout() {
        return this.httpClient.get<User>(urls.AuthUrls.logout).pipe(tap((authUser) => {
          let userEmail = '';
          let userPassword = '';
          userEmail = this.getEmail();
          userPassword = this.getPassword();
          encryptStorage.clear();
          if (this.isRememeberMe) {
            encryptStorage.setItem(AuthService.AUTH_USER_REMEMBERME, 'true');
            encryptStorage.setItem(AuthService.AUTH_USER_EMAIL, userEmail);
            encryptStorage.setItem(AuthService.AUTH_USER_PASSWORD, userPassword);
          }
          this.router.navigate([AuthRoutes.login]);
        }));
      }
    public isAuthenticated(): boolean {
        const token = encryptStorage.getItem(AuthService.AUTH_TOKEN);
        return token !== null;
    }
    showMessage() {
        this.modalService.error({
          nzTitle: 'Your Logged in credentials might be changed. So Please log in again with correct credentials',
          nzOkText: 'OK',
          nzOkType: 'danger',
          nzOnOk: () => this.modalService.closeAll()
        });
      }
      getUserGuide(){
        var params = {
            "appCode": this.getAppCode() === '' ? '*' : this.getAppCode(),
          "portalType":  "merchantboard",
        }
        return  this.httpClient.post(urls.userGuide.list, params);
      }
      getAttachment(value){
        return this.httpClient.post(urls.AttachmentUrls.listAttachments , value);
      }

      getDecryptModules() {
        if (this.getRoleId()) {
          const accessModules: any = this.getRoleAccessModule();
          this.accessModuleList = accessModules;
        } else {
          this.accessModuleList = [];
        }
      }
      clearDecryptModules() {
        this.accessModuleList = [];
      }
      getModuleGroupAndModuleAccess(moduleName: string, groupName: string) {
        if (this.accessModuleList.length > 0) {
          const index = this.accessModuleList.findIndex(M => M.backofficeModule.moduleName.toUpperCase() === moduleName.toUpperCase() && M.backofficeModule.groupName.toUpperCase() === groupName.toUpperCase());
          if (index !== -1) {
            return true;
          } else {
            return false;
          }
        } else {
          return false;
        }
      }

      getModuleGroup(groupName: string) {
        if (this.accessModuleList.length > 0) {
          const index = this.accessModuleList.findIndex(M => M.backofficeModule.groupName.toUpperCase() === groupName.toUpperCase());
          if (index !== -1) {
            return true;
          } else {
            return false;
          }
        } else {
          return false;
        }
      }

    
}
